/* eslint-disable no-nested-ternary */
import { getGroupedUserEventOptions } from '../utils';
import { USER_EVENT_STATUSES, USER_EVENT_ACTIVITY } from '../../../../constants';

const buildEventTypeDependecies = (
  eventTypes,
  allUsers,
  allTags,
  availableIotEventTypes,
  availableUserEventTypes,
  filterParams
) => {
  const groupedOptions = getGroupedUserEventOptions(availableUserEventTypes);
  switch (eventTypes) {
    case 'iot':
      return {
        eventType: {
          properties: {
            iotTypes: {
              default: filterParams?.iot?.eventType,
              title: 'Types',
              isMulti: true,
              uniqueItems: true,
              type: 'array',
              items: {
                type: 'string',
                anyOf: availableIotEventTypes.map((eventType) => ({
                  enum: [eventType.id],
                  title: eventType.name,
                })),
              },
            },
            active: {
              default: true,
              title: 'Hide inactive events',
              type: 'boolean',
            },
          },
        },
      };
    case 'user':
      return {
        eventType: {
          properties: {
            assignedTo: {
              default: filterParams?.user?.assignee,
              title: 'Assignee',
              type: 'array',
              uniqueItems: true,
              isMulti: true,
              items: {
                type: 'string',
                anyOf: allUsers.map((user) => ({
                  enum: [user.id],
                  title: user.name,
                })),
              },
            },
            users: {
              default: filterParams?.user?.creator,
              title: 'Creator',
              type: 'array',
              uniqueItems: true,
              isMulti: true,
              items: {
                type: 'string',
                anyOf: allUsers.map((user) => ({
                  enum: [user.id],
                  title: user.name,
                })),
              },
            },
            userTypes: {
              default: filterParams?.user?.type,
              title: 'Types',
              type: 'array',
              uniqueItems: true,
              isMulti: true,
              groupedOptions,
              items: {
                type: 'string',
                anyOf: availableUserEventTypes.map((eventType) => ({
                  enum: [eventType.id],
                  title: eventType.name,
                })),
              },
            },
            activity: {
              type: 'string',
              title: 'Activity',
              default: filterParams?.user?.activity || 'all',
              anyOf: [
                ...USER_EVENT_ACTIVITY.map((activity) => ({
                  enum: [activity.toLowerCase()],
                  title: activity,
                })),
              ],
            },
            status: {
              default: filterParams?.user?.status || 'all',
              type: 'string',
              title: 'Status',
              anyOf: [
                ...USER_EVENT_STATUSES.map((status) => ({
                  enum: [status.toLowerCase()],
                  title: status,
                })),
              ],
            },
            tags: {
              default: filterParams?.user?.tags,
              title: 'Tags',
              type: 'array',
              uniqueItems: true,
              isMulti: true,
              noOptionsMessage: 'No tags found',
              items: {
                type: 'string',
                anyOf: allTags.map((tag) => ({
                  enum: [tag.id],
                  title: tag.name,
                })),
              },
            },
          },
        },
      };
    case 'all':
    default:
      return {
        eventType: {
          oneOf: [
            {
              properties: {
                eventType: { enum: ['user'] },
                assignedTo: {
                  default: filterParams?.user?.assignee,
                  title: 'Assignee',
                  type: 'array',
                  placeholder: 'All',
                  uniqueItems: true,
                  isMulti: true,
                  items: {
                    type: 'string',
                    anyOf: allUsers.map((user) => ({
                      enum: [user.id],
                      title: user.name,
                    })),
                  },
                },
                users: {
                  default: filterParams?.user?.creator,
                  title: 'Creator',
                  type: 'array',
                  placeholder: 'All',
                  uniqueItems: true,
                  isMulti: true,
                  items: {
                    type: 'string',
                    anyOf: allUsers.map((user) => ({
                      enum: [user.id],
                      title: user.name,
                    })),
                  },
                },
                userTypes: {
                  default: filterParams?.user?.type,
                  title: 'Types',
                  type: 'array',
                  placeholder: 'All',
                  uniqueItems: true,
                  isMulti: true,
                  groupedOptions,
                  items: {
                    type: 'string',
                    anyOf: availableUserEventTypes.map((eventType) => ({
                      enum: [eventType.id],
                      title: eventType.name,
                    })),
                  },
                },
                activity: {
                  type: 'string',
                  title: 'Activity',
                  default: filterParams?.user?.activity || 'all',
                  anyOf: [
                    ...USER_EVENT_ACTIVITY.map((activity) => ({
                      enum: [activity.toLowerCase()],
                      title: activity,
                    })),
                  ],
                },
                status: {
                  default: filterParams?.user?.status || 'all',
                  type: 'string',
                  title: 'Status',
                  anyOf: [
                    ...USER_EVENT_STATUSES.map((status) => ({
                      enum: [status.toLowerCase()],
                      title: status,
                    })),
                  ],
                },
                tags: {
                  default: filterParams?.user?.tags,
                  title: 'Tags',
                  type: 'array',
                  placeholder: 'All',
                  uniqueItems: true,
                  isMulti: true,
                  noOptionsMessage: 'No tags found',
                  items: {
                    type: 'string',
                    anyOf: allTags.map((tag) => ({
                      enum: [tag.id],
                      title: tag.name,
                    })),
                  },
                },
              },
            },
            {
              properties: {
                eventType: { enum: ['iot'] },
                iotTypes: {
                  default: filterParams?.iot?.eventType,
                  title: 'Types',
                  isMulti: true,
                  uniqueItems: true,
                  type: 'array',
                  placeholder: 'All',
                  items: {
                    type: 'string',
                    anyOf: availableIotEventTypes.map((eventType) => ({
                      enum: [eventType.id],
                      title: eventType.name,
                    })),
                  },
                },
                active: {
                  default: true,
                  title: 'Hide inactive events',
                  type: 'boolean',
                },
              },
            },
          ],
        },
      };
  }
};

const buildExportContentDependencies = (
  eventTypeDependecies,
  eventTypes,
  filterParams,
  availableUserEventTypes
) =>
  eventTypes === 'iot' || availableUserEventTypes.filter((x) => x.task).length === 0
    ? {
        dependencies: {
          exportContent: {
            oneOf: [
              {
                properties: {
                  exportContent: { enum: ['events'] },
                  exportType: {
                    type: 'string',
                    title: 'Export format',
                    default: 'xlsx',
                    oneOf: [
                      { enum: ['xml'], title: '.xml' },
                      { enum: ['xlsx'], title: '.xlsx' },
                    ],
                  },
                  eventType: {
                    type: 'string',
                    title: 'Event group',
                    default: eventTypes === 'all' ? 'iot' : eventTypes === 'user' ? 'user' : 'iot',
                    oneOf:
                      eventTypes === 'all'
                        ? [
                            { enum: ['iot'], title: 'IoT' },
                            { enum: ['user'], title: 'User Events' },
                          ]
                        : eventTypes === 'user'
                          ? [{ enum: ['user'], title: 'User Events' }]
                          : [{ enum: ['iot'], title: 'IoT' }],
                  },
                },
              },
            ],
          },
          ...eventTypeDependecies,
        },
      }
    : {
        dependencies: {
          exportContent: {
            oneOf: [
              {
                properties: {
                  exportContent: { enum: ['events'] },
                  exportType: {
                    type: 'string',
                    title: 'Export format',
                    default: 'xlsx',
                    oneOf: [
                      { enum: ['xml'], title: '.xml' },
                      { enum: ['xlsx'], title: '.xlsx' },
                    ],
                  },
                  eventType: {
                    type: 'string',
                    title: 'Event group',
                    default: eventTypes === 'all' ? 'iot' : eventTypes === 'user' ? 'user' : 'iot',
                    oneOf:
                      eventTypes === 'all'
                        ? [
                            { enum: ['iot'], title: 'IoT' },
                            { enum: ['user'], title: 'User Events' },
                          ]
                        : eventTypes === 'user'
                          ? [{ enum: ['user'], title: 'User Events' }]
                          : [{ enum: ['iot'], title: 'IoT' }],
                  },
                },
              },
              {
                properties: {
                  exportContent: { enum: ['statistics'] },
                  taskTypes: {
                    default: filterParams?.user?.type,
                    title: 'Task Types',
                    type: 'array',
                    uniqueItems: true,
                    isMulti: true,
                    items: {
                      type: 'string',
                      anyOf: availableUserEventTypes
                        .filter((x) => x.task)
                        .map((eventType) => ({
                          enum: [eventType.id],
                          title: eventType.name,
                        })),
                    },
                  },
                },
              },
            ],
          },
          ...eventTypeDependecies,
        },
      };

const getEventSeriesExportSchema = (
  panelId,
  allUsers,
  allTags,
  eventTypes,
  availableIotEventTypes,
  availableUserEventTypes,
  filterParams,
  pickerId
) => {
  const eventTypeDependecies = buildEventTypeDependecies(
    eventTypes,
    allUsers,
    allTags,
    availableIotEventTypes,
    availableUserEventTypes,
    filterParams
  );

  const hasTags = allTags.length;

  const exportContentDependencies = buildExportContentDependencies(
    eventTypeDependecies,
    eventTypes,
    filterParams,
    availableUserEventTypes
  );

  const schema = {
    type: 'object',
    required: ['range', 'exportContent'],
    properties: {
      range: {
        title: 'Time Interval',
        default: null,
        panelId,
        pickerId,
      },
      exportContent: {
        type: 'string',
        title: 'Export Content',
        placeholder: 'Select content',
        oneOf:
          eventTypes === 'iot' || availableUserEventTypes.filter((x) => x.task).length === 0
            ? [{ enum: ['events'], title: 'Events' }]
            : [
                { enum: ['events'], title: 'Events' },
                { enum: ['statistics'], title: 'Statistics' },
              ],
      },
    },
    ...exportContentDependencies,
  };

  if (!hasTags) {
    delete schema.dependencies.exportContent.oneOf[0].eventType?.oneOf[0].properties.tags;
  }

  return schema;
};

const getUiSchema = () => ({
  range: {
    'ui:field': 'dateRangePicker',
  },
});

export { getUiSchema, getEventSeriesExportSchema };
